import React, { useState } from "react";
import { Package, Calendar, User, Clock, AlertCircle } from "lucide-react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";

const Bestilt: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  openCaseForEditing,
}) => {
  const [isMoving, setIsMoving] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const bestiltCases = cases.filter((case_) => case_.kategori === "Bestilt");

  // Filtrer basert på søk
  const filteredCases = bestiltCases.filter(
    (case_) =>
      case_.kunde.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.maskinMerke?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.kommentar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.varelinjer?.some(v => 
        v.varebeskrivelse?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.varenr?.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  // Sorter etter opprettet dato - eldste først
  const sortedCases = [...filteredCases].sort((a, b) => {
  const dateA = new Date(a.opprettet || a.dato).getTime();
  const dateB = new Date(b.opprettet || b.dato).getTime();
  return dateA - dateB; // Eldste først
});

  // Grupper ordre etter kunde
  const groupedCases = sortedCases.reduce((groups, case_) => {
    const customerName = case_.kunde;
    if (!groups[customerName]) {
      groups[customerName] = [];
    }
    groups[customerName].push(case_);
    return groups;
  }, {} as Record<string, typeof sortedCases>);

  const formatCurrency = (amount: number | string): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 2
    }).format(num || 0);
  };

  const handleMoveToKlarTilHenting = async (caseId: number) => {
    setIsMoving(caseId);
    const originalCases = [...cases];
    
    try {
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Sak ikke funnet");
      
      const today = new Date().toISOString().split("T")[0];
      const updatedCase = {
        ...caseToUpdate,
        kategori: "Klar til henting",
        utfortDato: today,
        serviceRepUtfort: "Direkte",
        opprettet: new Date().toISOString(),
      };
      
      setCases(prev =>
        prev.map(case_ =>
          case_.id === caseId ? updatedCase : case_
        )
      );
      
      const saved = await supabaseService.saveCase(updatedCase);
      if (!saved) {
        throw new Error("Kunne ikke lagre endring");
      }
      
      console.log(`✅ Flyttet sak ${caseId} direkte til Klar til henting`);
    } catch (error) {
      setCases(originalCases);
      console.error("Feil ved flytting av sak:", error);
    } finally {
      setIsMoving(null);
    }
  };

  const getDaysWaiting = (delebestillingDato: string): number => {
    if (!delebestillingDato) return 0;
    const orderDate = new Date(delebestillingDato);
    const now = new Date();
    return Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getWaitingBadge = (delebestillingDato: string) => {
    const days = getDaysWaiting(delebestillingDato);
    
    if (days > 14) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle size={12} />
          {days} dager
        </span>
      );
    }
    if (days > 7) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Clock size={12} />
          {days} dager
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <Clock size={12} />
        {days} {days === 1 ? 'dag' : 'dager'}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="text-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Bestilt
              </h2>
              <p className="text-sm text-gray-600">Venter på deler</p>
            </div>
          </div>
          <span className="px-3 py-1.5 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
            {filteredCases.length} {filteredCases.length === 1 ? 'sak' : 'saker'}
          </span>
        </div>
      </div>

      <div className="p-4">
        {/* Søkefelt */}
        <input
          type="text"
          placeholder="Søk i bestilte saker..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
        />

        {filteredCases.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Package className="text-gray-400" size={32} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? "Ingen treff" : "Ingen bestilte saker"}
            </h3>
            <p className="text-gray-600">
              {searchTerm ? "Prøv et annet søkeord" : "Saker med bestilte deler vil vises her"}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedCases).map(([customerName, customerCases]) => (
              <div key={customerName} className="border border-gray-200 rounded-lg">
                {/* Kunde header */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <User className="mr-2" size={18} />
                      {customerName}
                    </h3>
                    <span className="text-sm text-gray-600">
                      {customerCases.length} ordre
                    </span>
                  </div>
                </div>

                {/* Ordre for denne kunden */}
                <div className="divide-y divide-gray-200">
                  {customerCases.map((case_) => (
                    <div 
                      key={case_.id} 
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => openCaseForEditing(case_)}
                    >
                      {/* Ordre header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold text-gray-900">
                            Ordre #{case_.id}
                          </span>
                          <span className="text-sm text-gray-600">
                            {new Date(case_.dato).toLocaleDateString('nb-NO')}
                          </span>
                          {case_.delebestillingDato && getWaitingBadge(case_.delebestillingDato)}
                        </div>
                        <div className="flex items-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveToKlarTilHenting(case_.id);
                            }}
                            disabled={isMoving === case_.id}
                            className="px-3 py-1 text-sm bg-orange-600 text-white 
                                     rounded hover:bg-orange-700 transition-colors
                                     disabled:opacity-50"
                          >
                            {isMoving === case_.id ? "Flytter..." : "→ Klar til henting"}
                          </button>
                        </div>
                      </div>

                      {/* Ordre beskrivelse */}
                      {case_.beskrivelse && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            <span className="font-medium">Beskrivelse:</span> {case_.beskrivelse}
                          </p>
                        </div>
                      )}

                      {/* Maskin info */}
                      {case_.maskinMerke && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">Maskin:</span> {case_.maskinMerke}
                            {case_.serienr && <span> (S/N: {case_.serienr})</span>}
                            {case_.arsmodel && <span> - {case_.arsmodel}</span>}
                          </p>
                        </div>
                      )}

                      {/* Varelinjer tabell */}
                      {case_.varelinjer && case_.varelinjer.some(v => v.varebeskrivelse || v.varenr) && (
                        <div className="mt-3">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Bestilte deler:</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full border border-gray-200 rounded-lg">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 border-b">
                                    Varenr
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 border-b">
                                    Beskrivelse
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 border-b">
                                    Antall
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 border-b">
                                    Pris
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 border-b">
                                    Sum
                                  </th>
                                  {case_.varelinjer.some(v => v.levert) && (
                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 border-b">
                                      Status
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {case_.varelinjer
                                  .filter(v => v.varebeskrivelse || v.varenr)
                                  .map((linje, index) => (
                                  <tr key={index} 
                                      className={`text-sm ${
                                        linje.levert 
                                          ? 'bg-green-50 text-green-800' 
                                          : 'bg-white text-gray-900'
                                      }`}>
                                    <td className="px-3 py-2 font-mono text-xs">
                                      {linje.varenr}
                                    </td>
                                    <td className="px-3 py-2">
                                      {linje.varebeskrivelse}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      {linje.antall}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      {linje.inklMva ? formatCurrency(linje.inklMva) : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">
                                      {linje.sum ? formatCurrency(linje.sum) : '-'}
                                    </td>
                                    {case_.varelinjer.some(v => v.levert) && (
                                      <td className="px-3 py-2 text-center">
                                        {linje.levert ? (
                                          <span className="inline-flex items-center px-2 py-1 text-xs 
                                                         bg-green-100 text-green-800 rounded-full">
                                            ✓ Levert
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-1 text-xs 
                                                         bg-yellow-100 text-yellow-800 rounded-full">
                                            ⏳ Venter
                                          </span>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                                
                                {/* Total rad */}
                                <tr className="bg-gray-100 font-medium">
                                  <td colSpan={case_.varelinjer.some(v => v.levert) ? 4 : 3} 
                                      className="px-3 py-2 text-right">
                                    Total:
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {formatCurrency(
                                      case_.varelinjer
                                        .filter(v => v.varebeskrivelse || v.varenr)
                                        .reduce((sum, linje) => sum + (parseFloat(linje.sum) || 0), 0)
                                    )}
                                  </td>
                                  {case_.varelinjer.some(v => v.levert) && (
                                    <td className="px-3 py-2"></td>
                                  )}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Kommentar */}
                      {case_.kommentar && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                            <span className="font-medium">Kommentar:</span> {case_.kommentar}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Bestilt;